"use client";

import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { isSessionAuthError, useSession } from "@/hooks/use-session";
import { setUser, setUserLoading } from "@/redux/features/user-slice";
import { fetchRoles } from "@/redux/features/roles-slice";
import { loginSuccess, logout } from "@/redux/features/auth-slice";
import { resetCommandState } from "@/redux/features/command";
import { AppDispatch } from "@/redux/store";
import { clearCommandTeamQueryCache } from "@/utils/command-query-cache";

/** Bridges the React Query session (source of truth, refreshed server-side via the /api catch-all route) into the Redux user/roles/auth slices that the rest of the app already reads. */
export function UserDataRefresh() {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { data: user, isLoading, isSuccess, isError, error } = useSession();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    dispatch(setUserLoading(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    const userId = user?.id ?? null;
    // Only reset when switching accounts — not on initial auth hydration (null → userId).
    if (
      prevUserIdRef.current !== undefined &&
      prevUserIdRef.current !== null &&
      userId !== null &&
      prevUserIdRef.current !== userId
    ) {
      clearCommandTeamQueryCache(queryClient);
      dispatch(resetCommandState());
    }
    prevUserIdRef.current = userId;
  }, [user?.id, queryClient, dispatch]);

  useEffect(() => {
    if (isSuccess && user) {
      dispatch(setUser(user));
      dispatch(loginSuccess());
      dispatch(fetchRoles());
    } else if (isError && (!user || isSessionAuthError(error))) {
      // A background refetch that fails for a transient reason (often right after a tab comes back)
      // keeps the signed-in user and the Command chat; only a failed first load or an auth error logs out.
      dispatch(setUser(null));
      dispatch(logout());
      dispatch(resetCommandState());
      clearCommandTeamQueryCache(queryClient);
    }
  }, [isSuccess, isError, error, user, dispatch, queryClient]);

  return null;
}
